import { withAuthenticationRequired } from "@auth0/auth0-react";
import { store } from "../store";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeviceBrandsTable from "../components/tables/DeviceBrands";
import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Sensors,
  WaterDrop,
  ElectricBolt,
  Thermostat,
  Close,
  CloudUpload,
  CheckCircle,
  Monitor,
  ToggleOn,
  DevicesOther,
  InsertDriveFile,
  Delete,
  TouchApp,
  SensorDoor,
} from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { postData } from "../API/Post";
import { putData } from "../API/Put";
import deleteData from "../API/Delete";
import fetchData from "../API/Fetch";

// Icône selon le nom du type
const getDeviceTypeIcon = (typeName) => {
  const name = typeName?.toLowerCase() || "";
  if (name.includes("water") || name.includes("eau")) {
    return <WaterDrop sx={{ color: "#6b9dad" }} />;
  }
  if (name.includes("elec") || name.includes("power")) {
    return <ElectricBolt sx={{ color: "#c4a574" }} />;
  }
  if (name.includes("temp") || name.includes("thermo")) {
    return <Thermostat sx={{ color: "#e07a5f" }} />;
  }
  if (name.includes("screen") || name.includes("écran") || name.includes("display")) {
    return <Monitor sx={{ color: "#8a9ba8" }} />;
  }
  if (name.includes("switch") || name.includes("relay")) {
    return <ToggleOn sx={{ color: "#9c88b8" }} />;
  }
  if (name.includes("button") || name.includes("bouton")) {
    return <TouchApp sx={{ color: "#d4a574" }} />;
  }
  if (name.includes("door") || name.includes("porte") || name.includes("contact")) {
    return <SensorDoor sx={{ color: "#7eb8c4" }} />;
  }
  return <DevicesOther sx={{ color: "#7a8a7a" }} />;
};

// Valeurs par défaut selon le type (basé sur le nom)
const getDefaultFields = (typeName) => {
  const name = typeName?.toLowerCase() || "";
  if (name.includes("water") || name.includes("eau")) {
    return { field: "total_liters", displayfield: "total_liters", aggregation: "sum" };
  }
  if (name.includes("elec") || name.includes("power")) {
    return { field: "total_kwh", displayfield: "total_kwh", aggregation: "sum" };
  }
  if (name.includes("temp") || name.includes("thermo")) {
    return { field: "temperature", displayfield: "temperature", aggregation: "last" };
  }
  if (name.includes("button") || name.includes("bouton")) {
    return { field: "button_status", displayfield: "button_status", aggregation: "last" };
  }
  if (name.includes("door") || name.includes("porte") || name.includes("contact")) {
    return { field: "door_status", displayfield: "door_status", aggregation: "last" };
  }
  return { field: "", displayfield: "", aggregation: "sum" };
};

// Générer le measurement depuis le nom
const generateMeasurement = (name) => {
  if (!name) return "";
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, "_")
    .trim();
};

function DeviceBrands() {
  const { t } = useTranslation("common");

  const userDB = useSelector((state) => state.userDB);
  const deviceTypes = useSelector((state) => state.deviceTypes);

  // State local pour les device brands (avec has_decoder depuis l'API admin)
  const [deviceBrands, setDeviceBrands] = useState([]);
  const [availableDecoders, setAvailableDecoders] = useState([]);
  const [selectedDecoder, setSelectedDecoder] = useState("");

  // Charger les device brands depuis l'endpoint admin (inclut has_decoder)
  // Fallback vers l'endpoint standard si CORS bloque l'admin
  const fetchAdminDeviceBrands = useCallback(async () => {
    try {
      // Essayer d'abord l'endpoint admin (avec has_decoder)
      const result = await fetchData("/api/admin/device-brands/");
      if (result) {
        setDeviceBrands(result);
        return;
      }
    } catch (error) {
      console.warn("Endpoint admin non disponible, fallback vers /device-brands/:", error);
    }

    // Fallback vers l'endpoint standard
    try {
      const result = await fetchData("/device-brands/");
      setDeviceBrands(result || []);
    } catch (error) {
      console.error("Erreur chargement device brands:", error);
    }
  }, []);

  // Charger la liste des décodeurs disponibles
  const fetchAvailableDecoders = useCallback(async () => {
    try {
      const result = await fetchData("/api/admin/device-brands/decoders");
      setAvailableDecoders(result || []);
    } catch (error) {
      console.warn("Erreur chargement décodeurs:", error);
    }
  }, []);

  // Charger les données au montage
  useEffect(() => {
    fetchAdminDeviceBrands();
    fetchAvailableDecoders();
  }, [fetchAdminDeviceBrands, fetchAvailableDecoders]);

  // États pour les modals
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [decoderFile, setDecoderFile] = useState(null);
  const fileInputRef = useRef(null);

  // Form pour création/édition
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      name: "",
      device_type_id: "",
      influx_measurement: "",
      field: "",
      displayfield: "",
      aggregation: "sum",
    },
  });

  // Form pour test décodeur
  const {
    control: testControl,
    handleSubmit: handleTestSubmit,
    reset: resetTest,
  } = useForm({
    defaultValues: {
      payload_hex: "",
      fport: 1,
    },
  });

  const watchName = watch("name");
  const watchDeviceType = watch("device_type_id");

  // Auto-génération du measurement
  useEffect(() => {
    if (!selectedBrand && watchName) {
      setValue("influx_measurement", generateMeasurement(watchName));
    }
  }, [watchName, selectedBrand, setValue]);

  // Auto-remplissage du field et aggregation selon le type
  useEffect(() => {
    if (!selectedBrand && watchDeviceType) {
      const selectedType = deviceTypes?.find((dt) => dt.id === watchDeviceType);
      if (selectedType) {
        const defaults = getDefaultFields(selectedType.name);
        setValue("field", defaults.field);
        setValue("displayfield", defaults.displayfield);
        setValue("aggregation", defaults.aggregation);
      }
    }
  }, [watchDeviceType, selectedBrand, setValue, deviceTypes]);

  useEffect(() => {
    store.dispatch({
      type: "pageTitle/update",
      payload: "Gestion des Capteurs",
    });
    let pageActions = [];

    if (userDB && userDB.admin) {
      pageActions = [
        {
          name: "+ Ajouter un capteur",
          icon: "Add",
          action: () => handleOpenEditModal(null),
        },
      ];
    }

    store.dispatch({ type: "pageActions/update", payload: pageActions });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, userDB]);

  // Fonction pour obtenir le nom du type depuis l'ID
  const getTypeName = (deviceTypeId) => {
    const dt = deviceTypes?.find((t) => t.id === deviceTypeId);
    return dt?.name?.toLowerCase() || "";
  };

  // Calcul des statistiques basé sur le nom du type
  const totalBrands = deviceBrands?.length || 0;
  const waterBrands =
    deviceBrands?.filter((b) => {
      const typeName = getTypeName(b.device_type_id);
      return typeName.includes("water") || typeName.includes("eau");
    }).length || 0;
  const electricBrands =
    deviceBrands?.filter((b) => {
      const typeName = getTypeName(b.device_type_id);
      return typeName.includes("elec") || typeName.includes("power");
    }).length || 0;
  const tempBrands =
    deviceBrands?.filter((b) => {
      const typeName = getTypeName(b.device_type_id);
      return typeName.includes("temp") || typeName.includes("thermo");
    }).length || 0;
  const buttonBrands =
    deviceBrands?.filter((b) => {
      const typeName = getTypeName(b.device_type_id);
      return typeName.includes("button") || typeName.includes("bouton");
    }).length || 0;
  const doorSensorBrands =
    deviceBrands?.filter((b) => {
      const typeName = getTypeName(b.device_type_id);
      return typeName.includes("door") || typeName.includes("porte") || typeName.includes("contact");
    }).length || 0;

  const statsCards = [
    {
      title: t("common.total"),
      value: totalBrands,
      icon: Sensors,
      color: "#78a882",
    },
    { title: "Eau", value: waterBrands, icon: WaterDrop, color: "#6b9dad" },
    {
      title: "Électricité",
      value: electricBrands,
      icon: ElectricBolt,
      color: "#c4a574",
    },
    {
      title: "Température",
      value: tempBrands,
      icon: Thermostat,
      color: "#e07a5f",
    },
    {
      title: "Boutons",
      value: buttonBrands,
      icon: TouchApp,
      color: "#d4a574",
    },
    {
      title: "Contacteurs",
      value: doorSensorBrands,
      icon: SensorDoor,
      color: "#7eb8c4",
    },
  ];

  // Handlers pour les modals
  const handleOpenEditModal = useCallback(
    (brand) => {
      setSelectedBrand(brand);
      setSelectedDecoder(brand?.decoder_file || "");
      setDecoderFile(null);
      if (brand) {
        reset({
          name: brand.name || "",
          device_type_id: brand.device_type_id || "",
          influx_measurement: brand.influx_measurement || "",
          field: brand.field || "",
          displayfield: brand.displayfield || brand.field || "",
          aggregation: brand.aggregation || "sum",
        });
      } else {
        reset({
          name: "",
          device_type_id: "",
          influx_measurement: "",
          field: "",
          displayfield: "",
          aggregation: "sum",
        });
      }
      setEditModalOpen(true);
    },
    [reset]
  );

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedBrand(null);
    setDecoderFile(null);
    setSelectedDecoder("");
    reset();
  };

  // Gestion du fichier décodeur
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Vérifier l'extension
      if (!file.name.endsWith(".py")) {
        store.dispatch({
          type: "snackbar/update",
          payload: {
            open: true,
            message: "Seuls les fichiers .py sont acceptés",
            severity: "error",
          },
        });
        return;
      }
      // Vérifier la taille (max 100Ko)
      if (file.size > 100 * 1024) {
        store.dispatch({
          type: "snackbar/update",
          payload: {
            open: true,
            message: "Le fichier ne doit pas dépasser 100Ko",
            severity: "error",
          },
        });
        return;
      }
      setDecoderFile(file);
    }
  };

  const handleRemoveFile = () => {
    setDecoderFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Upload du fichier décodeur
  const uploadDecoder = async (deviceBrandId) => {
    if (!decoderFile) return true; // Pas de fichier = OK

    const formData = new FormData();
    formData.append("file", decoderFile);

    try {
      const response = await fetch(
        `${window.location.origin}/api/admin/device-brands/${deviceBrandId}/upload-decoder`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        return true;
      } else {
        const error = await response.json();
        throw new Error(error.message || "Erreur upload décodeur");
      }
    } catch (error) {
      store.dispatch({
        type: "snackbar/update",
        payload: {
          open: true,
          message: `Erreur upload: ${error.message}`,
          severity: "error",
        },
      });
      return false;
    }
  };

  const handleOpenTestModal = (brand) => {
    setSelectedBrand(brand);
    setTestResult(null);
    setTestError(null);
    resetTest();
    setTestModalOpen(true);
  };

  const handleCloseTestModal = () => {
    setTestModalOpen(false);
    setSelectedBrand(null);
    setTestResult(null);
    setTestError(null);
  };

  const handleOpenDeleteModal = (brand) => {
    setSelectedBrand(brand);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedBrand(null);
  };

  // Soumission du formulaire de création/édition
  const onSubmitEdit = async (data) => {
    setLoading(true);
    try {
      let response;
      let deviceBrandId;

      // Ajouter le decoder_file si sélectionné depuis la liste
      const submitData = { ...data };
      if (selectedDecoder) {
        submitData.decoder_file = selectedDecoder;
      }

      if (selectedBrand) {
        response = await putData(
          `/api/admin/device-brands/${selectedBrand.id}`,
          submitData
        );
        deviceBrandId = selectedBrand.id;
      } else {
        response = await postData("/api/admin/device-brands/", submitData);
        deviceBrandId = response?.data?.id;
      }

      if (response && (response.status === 200 || response.status === 201)) {
        // Upload du décodeur si un fichier est sélectionné (priorité sur la liste)
        let decoderUploaded = false;
        if (decoderFile && deviceBrandId) {
          decoderUploaded = await uploadDecoder(deviceBrandId);
        }

        // Message de succès
        let successMsg = selectedBrand
          ? "Capteur modifié avec succès"
          : "Capteur créé avec succès";
        if (decoderUploaded) {
          successMsg += " - Décodeur uploadé !";
        } else if (selectedDecoder) {
          successMsg += " - Décodeur assigné !";
        }

        store.dispatch({
          type: "snackbar/update",
          payload: {
            open: true,
            message: successMsg,
            severity: "success",
          },
        });

        // Rafraîchir les données depuis l'API admin
        await fetchAdminDeviceBrands();
        handleCloseEditModal();
      } else {
        throw new Error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      store.dispatch({
        type: "snackbar/update",
        payload: {
          open: true,
          message: `Erreur: ${error.message}`,
          severity: "error",
        },
      });
    }
    setLoading(false);
  };

  // Test du décodeur
  const onSubmitTest = async (data) => {
    setLoading(true);
    setTestResult(null);
    setTestError(null);

    try {
      const response = await postData(
        `/api/admin/device-brands/${selectedBrand.id}/test-decoder`,
        data
      );

      if (response?.data?.success) {
        setTestResult(response.data.result);
      } else {
        setTestError(response?.data?.error || "Erreur de décodage");
      }
    } catch (error) {
      setTestError(error.message || "Erreur lors du test");
    }
    setLoading(false);
  };

  // Suppression
  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await deleteData(
        `/api/admin/device-brands/${selectedBrand.id}`
      );

      if (response && response.status === 200) {
        store.dispatch({
          type: "snackbar/update",
          payload: {
            open: true,
            message: "Capteur supprimé avec succès",
            severity: "success",
          },
        });
        await fetchAdminDeviceBrands();
        handleCloseDeleteModal();
      }
    } catch (error) {
      store.dispatch({
        type: "snackbar/update",
        payload: {
          open: true,
          message: `Erreur: ${error.message}`,
          severity: "error",
        },
      });
    }
    setLoading(false);
  };

  // Vérification admin
  if (!userDB?.admin) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h5" color="error">
          Accès non autorisé
        </Typography>
        <Typography color="text.secondary">
          Cette page est réservée aux administrateurs.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {/* Cartes KPI */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          {statsCards.map((card, index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                flex: "1 1 180px",
                minWidth: 180,
                background: alpha(card.color, 0.15),
                borderLeft: `4px solid ${card.color}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <card.icon sx={{ color: card.color, fontSize: 32 }} />
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ color: card.color, fontWeight: "bold" }}
                  >
                    {card.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {card.title}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </Grid>

      {/* Info API */}
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Les endpoints API backend seront disponibles sous{" "}
          <code>/api/admin/device-brands/</code>. En attendant, l'interface est
          prête.
        </Alert>
      </Grid>

      {/* Tableau */}
      <Grid item xs={12}>
        <DeviceBrandsTable
          deviceBrands={deviceBrands}
          onEdit={handleOpenEditModal}
          onTest={handleOpenTestModal}
          onDelete={handleOpenDeleteModal}
        />
      </Grid>

      {/* Modal Création/Édition */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          {selectedBrand ? "Modifier le capteur" : "Ajouter un nouveau capteur"}
          <IconButton onClick={handleCloseEditModal} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmitEdit)}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              {/* Nom */}
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true, minLength: 3, maxLength: 50 }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Nom du capteur *"
                      fullWidth
                      error={!!error}
                      helperText={
                        error
                          ? "Le nom est requis (3-50 caractères)"
                          : "Ex: Aquametro, Radio EVO, Milesight CT101"
                      }
                    />
                  )}
                />
              </Grid>

              {/* Type */}
              <Grid item xs={12}>
                <Controller
                  name="device_type_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth error={!!error}>
                      <InputLabel>Type de capteur *</InputLabel>
                      <Select {...field} label="Type de capteur *">
                        {deviceTypes?.map((dt) => (
                          <MenuItem key={dt.id} value={dt.id}>
                            <Box
                              sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                              {getDeviceTypeIcon(dt.name)}
                              {dt.name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Measurement */}
              <Grid item xs={12}>
                <Controller
                  name="influx_measurement"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Measurement InfluxDB *"
                      fullWidth
                      error={!!error}
                      helperText="Auto-généré depuis le nom (snake_case)"
                      InputProps={{
                        sx: { fontFamily: "monospace" },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Field */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="field"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Champ principal *"
                      fullWidth
                      error={!!error}
                      helperText="Ex: total_liters, total_kwh"
                      InputProps={{
                        sx: { fontFamily: "monospace" },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Displayfield */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="displayfield"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Champ d'affichage"
                      fullWidth
                      helperText="Défaut: même que champ principal"
                      InputProps={{
                        sx: { fontFamily: "monospace" },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Aggregation */}
              <Grid item xs={12}>
                <Controller
                  name="aggregation"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Agrégation</InputLabel>
                      <Select {...field} label="Agrégation">
                        <MenuItem value="sum">Somme (sum)</MenuItem>
                        <MenuItem value="last">Dernière valeur (last)</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Sélection décodeur existant */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                  Décodeur
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Choisir un décodeur existant</InputLabel>
                  <Select
                    value={selectedDecoder}
                    onChange={(e) => {
                      setSelectedDecoder(e.target.value);
                      if (e.target.value) {
                        setDecoderFile(null); // Réinitialiser le fichier uploadé
                      }
                    }}
                    label="Choisir un décodeur existant"
                  >
                    <MenuItem value="">
                      <em>Aucun</em>
                    </MenuItem>
                    {availableDecoders.map((decoder) => (
                      <MenuItem key={decoder} value={decoder}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <InsertDriveFile sx={{ fontSize: 18, color: "#78a882" }} />
                          {decoder}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* OU Upload nouveau décodeur */}
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ display: "block", textAlign: "center", mb: 1, color: "text.secondary" }}>
                  — ou uploader un nouveau fichier —
                </Typography>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".py"
                  style={{ display: "none" }}
                />
                {decoderFile ? (
                  <Paper
                    sx={{
                      p: 2,
                      border: "2px solid #78a882",
                      backgroundColor: "rgba(120, 168, 130, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <InsertDriveFile sx={{ color: "#78a882" }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {decoderFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(decoderFile.size / 1024).toFixed(1)} Ko
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={handleRemoveFile}
                      sx={{ color: "#a57474" }}
                    >
                      <Delete />
                    </IconButton>
                  </Paper>
                ) : (
                  <Paper
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      p: 2,
                      border: "2px dashed rgba(255,255,255,0.2)",
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "#78a882",
                        backgroundColor: "rgba(120, 168, 130, 0.05)",
                      },
                    }}
                  >
                    <CloudUpload sx={{ fontSize: 40, color: "#9aa89a", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Cliquez pour uploader un nouveau décodeur (.py)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Max 100Ko
                    </Typography>
                  </Paper>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <Button onClick={handleCloseEditModal}>Annuler</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#78a882",
                "&:hover": { backgroundColor: "#5d8a66" },
              }}
            >
              {loading ? (
                <CircularProgress size={20} />
              ) : selectedBrand ? (
                "Modifier"
              ) : (
                "Créer le capteur"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Modal Test Décodeur */}
      <Dialog
        open={testModalOpen}
        onClose={handleCloseTestModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          Tester le décodeur: {selectedBrand?.name}
          <IconButton onClick={handleCloseTestModal} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleTestSubmit(onSubmitTest)}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              {/* Payload */}
              <Grid item xs={12}>
                <Controller
                  name="payload_hex"
                  control={testControl}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Payload hexadécimal *"
                      fullWidth
                      error={!!error}
                      helperText="Ex: 000000ca"
                      InputProps={{
                        sx: { fontFamily: "monospace" },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* FPort */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="fport"
                  control={testControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="FPort"
                      type="number"
                      fullWidth
                      InputProps={{
                        inputProps: { min: 1, max: 255 },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Résultat */}
              {(testResult || testError) && (
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: testError
                        ? "rgba(165, 116, 116, 0.1)"
                        : "rgba(120, 168, 130, 0.1)",
                      borderLeft: `4px solid ${testError ? "#a57474" : "#78a882"}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        color: testError ? "#a57474" : "#78a882",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {testError ? "Erreur" : <><CheckCircle fontSize="small" /> Résultat</>}
                    </Typography>
                    {testError ? (
                      <Typography color="error">{testError}</Typography>
                    ) : (
                      <Box
                        component="pre"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                          m: 0,
                          overflow: "auto",
                        }}
                      >
                        {JSON.stringify(testResult, null, 2)}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <Button onClick={handleCloseTestModal}>Fermer</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#78a882",
                "&:hover": { backgroundColor: "#5d8a66" },
              }}
            >
              {loading ? <CircularProgress size={20} /> : "Tester le décodeur"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Modal Suppression */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le capteur{" "}
            <strong>{selectedBrand?.name}</strong> ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Annuler</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Supprimer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default withAuthenticationRequired(DeviceBrands);
